export interface StoreAddress {
  jibunAddress?: string;
  roadAddress?: string;
  detailAddress?: string;
  // 매장 좌표 (dev 응답 기준 문자열로 내려옴) — 반경/그리기 렌더링 기준점 폴백용
  latitude?: number | string | null;
  longitude?: number | string | null;
}

export type BusinessHourType = 'DAILY' | 'WEEKDAY_WEEKEND' | 'DAY_OF_WEEK';
export type BreakTimeType = 'DAILY' | 'WEEKDAY_WEEKEND' | 'DAY_OF_WEEK';
export type HolidayType = 'MONTHLY_WEEK_AND_DAY' | 'SPECIFIC_DATE' | 'WEEKLY_DAY' | 'MONTHLY_DATE'; // WEEKLY_DAY, MONTHLY_DATE: 크롤링 데이터에서 사용
export type StoreOrderType = 'DELIVERY' | 'TAKEOUT' | 'TABLE' | 'BOTH';

export type CorporateType = keyof typeof CORPORATE_TYPES;
export const DELIVERY_SCOPE_TYPES = {
  RADIUS: 'RADIUS',
  REGION: 'REGION',
  POLYGON: 'POLYGON',
  NONE: 'NONE',
} as const;

// Value 를 타입으로 추출
export type DeliveryScopeType = (typeof DELIVERY_SCOPE_TYPES)[keyof typeof DELIVERY_SCOPE_TYPES];
export const DELIVERY_SCOPE_TYPE_LABELS: Record<DeliveryScopeType, string> = {
  [DELIVERY_SCOPE_TYPES.RADIUS]: '반경',
  [DELIVERY_SCOPE_TYPES.REGION]: '지역',
  [DELIVERY_SCOPE_TYPES.POLYGON]: '범위 (그리기)',
  [DELIVERY_SCOPE_TYPES.NONE]: '',
} as const;

// 배달 방식 (사장님웹 DeliveryMethodType과 동일 스펙)
export const DELIVERY_METHOD_TYPES = {
  STORE_DELIVERY: 'STORE_DELIVERY',
  DELIVERY_AGENCY: 'DELIVERY_AGENCY',
  DELIVERY_COMBINED: 'DELIVERY_COMBINED',
} as const;

export type DeliveryMethodType = (typeof DELIVERY_METHOD_TYPES)[keyof typeof DELIVERY_METHOD_TYPES];
export const DELIVERY_METHOD_TYPE_LABELS: Record<DeliveryMethodType, string> = {
  [DELIVERY_METHOD_TYPES.STORE_DELIVERY]: '직접배달',
  [DELIVERY_METHOD_TYPES.DELIVERY_AGENCY]: '배달대행',
  [DELIVERY_METHOD_TYPES.DELIVERY_COMBINED]: '직접배달 + 배달대행',
} as const;

// 요일 (서버 스펙: ["MONDAY", ...])
export const DAY_OF_WEEK_LABELS = {
  MONDAY: '월',
  TUESDAY: '화',
  WEDNESDAY: '수',
  THURSDAY: '목',
  FRIDAY: '금',
  SATURDAY: '토',
  SUNDAY: '일',
} as const;

export type DayOfWeekType = keyof typeof DAY_OF_WEEK_LABELS;

export const CORPORATE_TYPES = {
  ADMIN: '꼬르륵 본사',
  BONSA: '본사',
  BONBU: '본부',
  CENTER: '센터',
  JISA: '지사',
  JIJUM: '지점',
} as const;

export interface BusinessHour {
  businessHourType: BusinessHourType;
  mondayStartTime?: string[];
  mondayEndTime?: string[];
  mondayAllDay: boolean;
  tuesdayStartTime?: string[];
  tuesdayEndTime?: string[];
  tuesdayAllDay: boolean;
  wednesdayStartTime?: string[];
  wednesdayEndTime?: string[];
  wednesdayAllDay: boolean;
  thursdayStartTime?: string[];
  thursdayEndTime?: string[];
  thursdayAllDay: boolean;
  fridayStartTime?: string[];
  fridayEndTime?: string[];
  fridayAllDay: boolean;
  saturdayStartTime?: string[];
  saturdayEndTime?: string[];
  saturdayAllDay: boolean;
  sundayStartTime?: string[];
  sundayEndTime?: string[];
  sundayAllDay: boolean;

  // 종료시간이 다음날(익일)인지 여부 — 사장님웹 businessHour와 동일 필드 (서버 추가 요청)
  mondayEndIsNext?: boolean | null;
  tuesdayEndIsNext?: boolean | null;
  wednesdayEndIsNext?: boolean | null;
  thursdayEndIsNext?: boolean | null;
  fridayEndIsNext?: boolean | null;
  saturdayEndIsNext?: boolean | null;
  sundayEndIsNext?: boolean | null;
}

export interface BreakTime {
  breakTimeType: BreakTimeType;
  mondayStartTime?: string[];
  mondayEndTime?: string[];
  tuesdayStartTime?: string[];
  tuesdayEndTime?: string[];
  wednesdayStartTime?: string[];
  wednesdayEndTime?: string[];
  thursdayStartTime?: string[];
  thursdayEndTime?: string[];
  fridayStartTime?: string[];
  fridayEndTime?: string[];
  saturdayStartTime?: string[];
  saturdayEndTime?: string[];
  sundayStartTime?: string[];
  sundayEndTime?: string[];

  // 종료시간이 다음날(익일)인지 여부 — 사장님웹 breakTime과 동일 필드 (서버 추가 요청)
  mondayEndIsNext?: boolean | null;
  tuesdayEndIsNext?: boolean | null;
  wednesdayEndIsNext?: boolean | null;
  thursdayEndIsNext?: boolean | null;
  fridayEndIsNext?: boolean | null;
  saturdayEndIsNext?: boolean | null;
  sundayEndIsNext?: boolean | null;

  // 시작시간이 다음날(익일)인지 여부 — 자정 넘김 영업의 새벽 브레이크타임 케이스 (dev 응답에 존재)
  mondayStartIsNext?: boolean | null;
  tuesdayStartIsNext?: boolean | null;
  wednesdayStartIsNext?: boolean | null;
  thursdayStartIsNext?: boolean | null;
  fridayStartIsNext?: boolean | null;
  saturdayStartIsNext?: boolean | null;
  sundayStartIsNext?: boolean | null;
}

/**
 * 종료시간 익일 여부 판별
 * - 서버가 endIsNext 플래그를 주면 그 값을 그대로 사용 (start==end 케이스 대응)
 * - 플래그가 없으면(구버전 응답) 종료 < 시작 휴리스틱으로 판별
 */
export const isNextDayEnd = (
  startTime?: string[],
  endTime?: string[],
  endIsNext?: boolean | null
): boolean => {
  if (typeof endIsNext === 'boolean') return endIsNext;
  if (!startTime || !endTime || startTime.length < 2 || endTime.length < 2) return false;

  const start = parseInt(startTime[0], 10) * 60 + parseInt(startTime[1], 10);
  const end = parseInt(endTime[0], 10) * 60 + parseInt(endTime[1], 10);
  if (isNaN(start) || isNaN(end)) return false;

  return end < start;
};

type SpecificDate = [number, number, number];

export interface Holiday {
  holidayType: HolidayType;
  daysOfWeek: dayKey[];
  daysOfMonth: number[];
  weeksOfMonth: WeekKey[];
  specificDates: SpecificDate[];
}

export interface DeliveryArea {
  addressRegion1Name: string;
  addressRegion2Name: string;
  addressRegion3Name: string;
  addressRegion4Name?: string;
}

export interface StoreDeliveryAreas {
  // baseDeliveryFee: number;
  deliveryAreas: DeliveryArea[];
}

export interface DeliveryCheck {
  freeDeliveryMode: boolean;
  // 무료 배달 모드 시 최소 주문금액 (서버 추가 요청 필드)
  minOrderPriceForFreeDelivery?: number;
}

// LocalTime 직렬화 값: [시, 분(, 초)] 배열 또는 "HH:mm(:ss)" 문자열
export type LocalTimeValue = number[] | string;

// ---------- 시간/요일별 추가 배달요금 ----------
// 서버 DeliveryTimeFeeRuleResponse (2026-07 확정 스펙)
export interface DeliveryTimeFeeRule {
  id?: number;
  daysOfWeek: DayOfWeekType[];
  startDate: LocalTimeValue; // 적용시작 시간 (LocalTime)
  endDate: LocalTimeValue; // 적용마감 시간 (LocalTime)
  extraDeliveryFee?: number | null; // 할증 금액 (서버 default 0)
}

// ---------- 날씨별 추가 배달요금 ----------
// 서버 StoreWeatherSurchargeResponseDto (2026-07 확정 스펙)
export interface DeliveryWeatherFeePolicy {
  id?: number | null; // storeDeliveryFeePolicyId
  weatherSurchargeEnabled?: boolean | null;
  weatherSurchargeFee?: number | null;
}

// ---------- 배달범위 그리기 ----------
// 서버 DeliveryZoneResponse (2026-07 확정 스펙)
// ※ 좌표 키는 latitude/longitude (사장님웹 내부의 lat/lng와 다름 주의)
export interface LatLng {
  latitude: number;
  longitude: number;
}

// 서버 ShapeResponse — radius 없음. circle 타입도 paths(다각형 좌표)로 내려옴
export interface DeliveryZoneShape {
  id?: string | null;
  type: string; // 'polygon' | 'circle' 등
  center?: LatLng;
  paths: LatLng[][];
}

// 서버 RegionResponse
export interface DeliveryZoneRegion {
  id?: number;
  name: string;
  color?: string | null;
  deliveryFee: number;
  active: boolean;
  priority: number;
  shapes: DeliveryZoneShape[];
}

export interface DeliveryZone {
  storeId?: number;
  regions: DeliveryZoneRegion[];
  distanceFeeRules?: DistanceDeliveryFeeRule[];
}

// ---------- 배달 반경 다중 원 ----------
// 서버 DistanceDeliveryDto (2026-07 확정 스펙)
export interface RadiusDeliveryItem {
  id?: number;
  storeId?: number;
  radiusKm: number;
  isActive: boolean;
  name?: string | null;
}

export interface OrderAmountDeliveryFeeRule {
  orderAmountDeliveryFeeRuleId: number;
  orderAmountLowerLimit: number;
  extraDeliveryFee: number;
}

export interface StoreOrderAmountDeliveryFeeRule {
  orderAmountDeliveryFeeRules: OrderAmountDeliveryFeeRule[];
}

export interface StoreDeliveryAreaFeeRule {
  deliveryFeeRules: DeliveryAreaFeeRule[];
}

export interface StoreDistanceDeliveryFeeRule {
  deliveryFeeRules: DistanceDeliveryFeeRule[];
}

export interface DeliveryAreaFeeRule {
  deliveryAreaFeeRuleId: number;
  extraDeliveryFee: number;
  deliveryAreas: DeliveryArea[];
}

export interface DistanceDeliveryFeeRule {
  distanceDeliveryFeeRuleId: number;
  deliveryDistanceLowerLimit: number;
  extraFeeDistanceUnit: number;
  extraDeliveryFee: number;
}

export interface StoreModel {
  storeId: number;
  storeName: string;
  storeNumber?: string;
  storeAddress: StoreAddress;
  mainCategory: string[];
  businessRegistrationCertificateStoreName: string;
  ownerName?: string;
  businessRegistrationCertificateNumber: string;
  bankType?: string;
  bankNumber?: string;
  bankOwnerName?: string;
  alcohol: boolean;
  corporateName: string;
  corporateType: CorporateType;
  corporation: boolean;
  storeStatusType: string; // TODO 타입 만들기
  // storeOwnerComment?: string; // 가게소개
  // storeNotice: string; // 사장님 공지
  storeIngredients?: string; // 원산지
  shopInShop: boolean; // 샵인샵 여부

  // files
  businessDeclarationCertificateUrl?: string;
  businessRegistrationCertificateUrl?: string;
  bankFileUrl?: string;

  businessHourList?: BusinessHour;
  storeBreakTimeList: BreakTime[];
  storeHolidayList: Holiday[];
  storeOrderType: { storeOrderType: StoreOrderType };
  storeDeliveryAreas: StoreDeliveryAreas;
  deliveryCheck: DeliveryCheck;
  storeOrderAmountDeliveryFeeRule: StoreOrderAmountDeliveryFeeRule;
  storeDeliveryAreaFeeRule: StoreDeliveryAreaFeeRule;
  storeDistanceDeliveryFeeRule: StoreDistanceDeliveryFeeRule;

  // 반경
  //  배달 설정
  deliveryScopeType: DeliveryScopeType | undefined | null;
  //  배달 반경 km
  radiusKm: number | undefined | null;

  //  배달 방식
  deliveryMethodType?: DeliveryMethodType | null;
  //  선택된 배달대행사명
  deliveryAgencyName?: string | null;
  //  시간/요일별 추가 배달요금
  deliveryTimeFeeRules?: DeliveryTimeFeeRule[];
  //  날씨별 추가 배달요금
  deliveryWeatherFeePolicy?: DeliveryWeatherFeePolicy;
  //  배달범위 그리기 (deliveryScopeType === POLYGON)
  deliveryZone?: DeliveryZone;
  //  배달 반경 다중 원 (deliveryScopeType === RADIUS)
  radiusList?: RadiusDeliveryItem[];
  //  매장 좌표 (반경/그리기 도형 렌더링용)
  latitude?: number | string | null;
  longitude?: number | string | null;
  //  예약 주문 사용 여부
  reservationOrderEnabled?: boolean | null;
  //  배달 주문 시 만나서 결제 노출 여부
  showDeliveryFacePayment?: boolean | null;
  //  포장 주문 시 만나서 결제 노출 여부
  showTakeoutFacePayment?: boolean | null;
}

// 시간/요일별 요일 리스트 표기 ("월, 화, 수")
export const formatDaysOfWeek = (daysOfWeek: DayOfWeekType[]): string => {
  const order = Object.keys(DAY_OF_WEEK_LABELS) as DayOfWeekType[];
  return [...(daysOfWeek || [])]
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map((d) => DAY_OF_WEEK_LABELS[d] || d)
    .join(', ');
};

// LocalTime 직렬화 값 -> [시, 분] (서버 Jackson 설정에 따라 [22,0] 배열 또는 "22:00:00" 문자열)
export const parseLocalTime = (time?: LocalTimeValue | null): number[] | null => {
  if (Array.isArray(time) && time.length >= 2) {
    const h = Number(time[0]);
    const m = Number(time[1]);
    if (!isNaN(h) && !isNaN(m)) return [h, m];
    return null;
  }

  if (typeof time === 'string' && time.includes(':')) {
    const [h, m] = time.split(':').map((v) => parseInt(v, 10));
    if (!isNaN(h) && !isNaN(m)) return [h, m];
  }

  return null;
};

// LocalTime -> "09:00"
export const formatTimeArr = (time?: LocalTimeValue | null): string => {
  const parsed = parseLocalTime(time);
  if (!parsed) return '-';
  return `${String(parsed[0]).padStart(2, '0')}:${String(parsed[1]).padStart(2, '0')}`;
};

// 시간/요일별 배달요금 시간대 표기 — 사장님웹 formatTimeRange와 동일 (종료 <= 시작이면 익일)
export const formatTimeRange = (
  startDate?: LocalTimeValue | null,
  endDate?: LocalTimeValue | null
): string => {
  const start = parseLocalTime(startDate);
  const end = parseLocalTime(endDate);
  if (!start || !end) return '-';

  const startMinutes = start[0] * 60 + start[1];
  const endMinutes = end[0] * 60 + end[1];
  const endPrefix = endMinutes <= startMinutes ? '익일 ' : '';

  return `${formatTimeArr(start)} ~ ${endPrefix}${formatTimeArr(end)}`;
};

type AddressRegion4Group = {
  addressRegion1Name: string;
  addressRegion2Name: string;
  addressRegion3Name: string;
  addressRegion4Name: string[];
};

type AddressRegion3Group = {
  addressRegion1Name: string;
  addressRegion2Name: string;
  addressRegion3Name: string[];
};

type GroupedDeliveryArea = AddressRegion3Group | AddressRegion4Group;

export const groupDeliveryAreas = (areas: DeliveryArea[]): GroupedDeliveryArea[] => {
  const result: GroupedDeliveryArea[] = [];

  for (const area of areas) {
    if (area.addressRegion4Name) {
      const index = result.findIndex(
        (v) =>
          v.addressRegion1Name === area.addressRegion1Name &&
          v.addressRegion2Name === area.addressRegion2Name &&
          v.addressRegion3Name === area.addressRegion3Name
      );

      if (index === -1) {
        result.push({
          addressRegion1Name: area.addressRegion1Name,
          addressRegion2Name: area.addressRegion2Name,
          addressRegion3Name: area.addressRegion3Name,
          addressRegion4Name: [area.addressRegion4Name],
        });
        continue;
      }

      (result[index] as AddressRegion4Group).addressRegion4Name.push(area.addressRegion4Name);

      // 중복 제거
      // ex)
      // {
      //     "id": 506,
      //     "addressRegion1Name": "충청북도",
      //     "addressRegion2Name": "청주시",
      //     "addressRegion3Name": "흥덕구", <= 제거 대상
      //     "addressRegion4Name": "",
      //     "code": 4311300000
      // },
      // {
      //     "id": 507,
      //     "addressRegion1Name": "충청북도",
      //     "addressRegion2Name": "청주시",
      //     "addressRegion3Name": "흥덕구",
      //     "addressRegion4Name": "가경동",
      //     "code": 4311311300
      // },
      for (let i = 0; i < result.length; i++) {
        const v = result[i];

        if (
          v.addressRegion1Name === area.addressRegion1Name &&
          v.addressRegion2Name === area.addressRegion2Name &&
          Array.isArray(v.addressRegion3Name)
        ) {
          if (v.addressRegion3Name[0] == area.addressRegion3Name) {
            result.splice(i, 1);
            break;
          }

          const idx = v.addressRegion3Name.indexOf(area.addressRegion3Name);
          if (idx !== -1) {
            v.addressRegion3Name.splice(idx, 1);
          }

          break;
        }
      }

      continue;
    }

    // ----- 주소3까지 있는 경우 ----- //

    // 주소4 리스트를 갖는 그룹으로 추가된 적 있는 경우 - 얼리리턴
    const hasAddress4 = result.some(
      (v) =>
        v.addressRegion1Name === area.addressRegion1Name &&
        v.addressRegion2Name === area.addressRegion2Name &&
        v.addressRegion3Name === area.addressRegion3Name
    );
    if (hasAddress4) continue;

    const index = result.findIndex(
      (v) =>
        v.addressRegion1Name === area.addressRegion1Name &&
        v.addressRegion2Name === area.addressRegion2Name &&
        Array.isArray(v.addressRegion3Name)
    );

    if (index === -1) {
      result.push({
        addressRegion1Name: area.addressRegion1Name,
        addressRegion2Name: area.addressRegion2Name,
        addressRegion3Name: [area.addressRegion3Name],
      });
      continue;
    }

    (result[index] as AddressRegion3Group).addressRegion3Name.push(area.addressRegion3Name);
  }

  return result;
};

// 주차 매핑
export const WEEKS = {
  1: '첫째주',
  2: '둘째주',
  3: '셋째주',
  4: '넷째주',
  5: '다섯째주',
} as const;

export type WeekKey = keyof typeof WEEKS;
export type dayKey = keyof typeof DAYS;

// 요일 매핑
export const DAYS = {
  1: '월요일',
  2: '화요일',
  3: '수요일',
  4: '목요일',
  5: '금요일',
  6: '토요일',
  7: '일요일',
} as const;

export const validateBusinessHour = (allDay: boolean, startTime?: string[], endTime?: string[]) => {
  if (!allDay && (!startTime || startTime.length != 2 || !endTime || endTime.length != 2)) {
    return false;
  }

  return true;
};

export const validateBreakTime = (startTime?: string[], endTime?: string[]) => {
  if (!startTime || !endTime || startTime.length < 2 || endTime.length < 2) {
    return false;
  }

  return true;
};

export const validateSpecificDatesHoliday = (holiday: Holiday) => {
  // [시작일, 종료일]
  // 시작일/종료일 == [년, 월, 일]

  // 빈 배열인 경우
  if (holiday.specificDates.length < 1) {
    return false;
  }

  // 시작일이 유효하지 않은 경우
  if (holiday.specificDates[0].length != 3) {
    return false;
  }

  // 종료일이 있는 경우, 종료일 유효성 검사
  if (holiday.specificDates.length > 1 && holiday.specificDates[1].length != 3) {
    return false;
  }

  return true;
};

export const validateHoliday = (holiday: Holiday) => {
  switch (holiday.holidayType) {
    case 'SPECIFIC_DATE': // 2023-12-25
      return validateSpecificDatesHoliday(holiday);
    case 'WEEKLY_DAY': // 매주 월요일
      return holiday.daysOfWeek.length > 0;
    case 'MONTHLY_DATE': // 매월 1일
      return holiday.daysOfMonth.length > 0;
    case 'MONTHLY_WEEK_AND_DAY': // 매월 둘째주 화요일
      return holiday.weeksOfMonth.length > 0 && holiday.daysOfWeek.length > 0;
    default:
      return false;
  }
};

export const hasBusinessHour = (businessHour: BusinessHour) => {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ] as const;

  for (const day of days) {
    const allDay = businessHour[`${day}AllDay`];
    const startTime = businessHour[`${day}StartTime`];
    const endTime = businessHour[`${day}EndTime`];

    if (validateBusinessHour(allDay, startTime, endTime)) {
      return true;
    }
  }

  return false;
};

export const hasBreakTime = (breakTime: BreakTime) => {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ] as const;

  for (const day of days) {
    const startTime = breakTime[`${day}StartTime`];
    const endTime = breakTime[`${day}EndTime`];

    if (validateBreakTime(startTime, endTime)) {
      return true;
    }
  }

  return false;
};

export const hasHoliday = (storeHolidayList: Holiday[]) => {
  for (const holiday of storeHolidayList) {
    if (validateHoliday(holiday)) {
      return true;
    }
  }

  return false;
};
